import React, { useEffect, useState } from "react";
import Layout from "../../Layout";
import { useSelector } from "react-redux";
import PatientService from "../../Services/PatientService";
import { Spin } from "antd";

const ProcedureList = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const patientData = useSelector((state) => state?.auth?.patientData);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (patientData?.patientId) {
      fetchProcedures();
    }
  }, [patientData]);

  const countries = [
    { country_Id: "IN", country_Name: "India" },
  ];

  const fetchProcedures = async () => {
    try {
      const payload = {
        patientId: patientData?.patientId,
        status: 1,
      };

      const response = await PatientService.procedureReqList(payload);

      setData(response?.data || []);
    } catch (error) {
      console.error("Error fetching procedure requests:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="w-full max-w-full overflow-x-hidden px-2 sm:px-4">
        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Spin />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            {!isMobile && (
              <div className="w-full overflow-x-auto mt-8">
                <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                  <thead className="bg-primaryColor text-white">
                    <tr>
                      <th className="p-3 text-left whitespace-nowrap">
                        Patient Name
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Mobile
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Email
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        City
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Country
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Service Type
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Service Description
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Planned Date
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Preferred Hospital
                      </th>
                      <th className="p-3 text-left whitespace-nowrap">
                        Status
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {data?.length > 0 ? (
                      data.map((record, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-200 text-sm"
                        >
                          <td className="p-3">
                            <div>{record.patientName}</div>
                            <div className="text-xs text-gray-500">
                              {record.patientId}
                            </div>
                          </td>

                          <td className="p-3">{record.mobile}</td>

                          <td className="p-3">{record.email}</td>

                          <td className="p-3">{record.city}</td>

                          <td className="p-3">
                            {countries.find(
                              (country) =>
                                country.country_Id === record.country
                            )?.country_Name || "-"}
                          </td>

                          <td className="p-3">
                            {record.plannedService}
                          </td>

                          <td className="p-3">
                            {record.serviceDescription}
                          </td>

                          <td className="p-3">
                            {record.plannedDate}
                          </td>

                          <td className="p-3">
                            {record.preferredHospital || "-"}
                          </td>

                          <td className="p-3">
                            <span
                              className={`px-3 py-1 rounded-md text-sm ${
                                record.status === 1
                                  ? "bg-[#31E4A81A] text-green-600"
                                  : "bg-[#FF60601A] text-red-600"
                              }`}
                            >
                              {record.status === 1
                                ? "Completed"
                                : "Pending"}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="10"
                          className="text-center p-5 text-gray-500"
                        >
                          No data found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* Mobile Card View */}
            {isMobile && (
              <div className="w-full flex flex-col gap-4 mt-5">
                {data?.length > 0 ? (
                  data.map((record, index) => (
                    <div
                      key={index}
                      className="w-full bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
                    >
                      {/* Header */}
                      <div className="bg-primaryColor px-4 py-3">
                        <h3 className="text-white text-sm font-semibold break-words">
                          {record.patientName}
                        </h3>

                        <p className="text-white/70 text-xs mt-1 break-all">
                          {record.patientId}
                        </p>
                      </div>

                      {/* Body */}
                      <div className="p-4 space-y-3 text-sm">
                        <InfoRow
                          label="Mobile"
                          value={record.mobile}
                        />

                        <InfoRow
                          label="Email"
                          value={record.email}
                        />

                        <InfoRow
                          label="City"
                          value={record.city}
                        />

                        <InfoRow
                          label="Country"
                          value={
                            countries.find(
                              (country) =>
                                country.country_Id === record.country
                            )?.country_Name || "-"
                          }
                        />

                        <InfoRow
                          label="Service Type"
                          value={record.plannedService}
                        />

                        <InfoRow
                          label="Description"
                          value={record.serviceDescription}
                        />

                        <InfoRow
                          label="Planned Date"
                          value={record.plannedDate}
                        />

                        <InfoRow
                          label="Hospital"
                          value={record.preferredHospital || "-"}
                        />

                        {/* Status */}
                        <div className="flex items-center justify-between border-t pt-3">
                          <span className="text-gray-500 font-medium">
                            Status
                          </span>

                          <span
                            className={`px-3 py-1 rounded-md text-sm ${
                              record.status === 1
                                ? "bg-[#31E4A81A] text-green-600"
                                : "bg-[#FF60601A] text-red-600"
                            }`}
                          >
                            {record.status === 1
                              ? "Completed"
                              : "Pending"}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-10 text-gray-500">
                    No data found
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

/* Reusable Row Component */
const InfoRow = ({ label, value }) => {
  return (
    <div className="flex flex-col gap-1 w-full">
      <span className="text-gray-500 text-xs font-medium">
        {label}
      </span>

      <span className="text-[#333] break-words whitespace-normal">
        {value || "-"}
      </span>
    </div>
  );
};

export default ProcedureList;